/*
 * File: LeavePageWidget.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 28, 2025 at 3:52 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useTranslation } from 'react-i18next';

// Services
import { recordAction } from '../../services/action';

// Components
import { Padding } from '../Padding';
import { Typography } from '../Typography';
import { LocaleLink } from '../LocaleLink';

// Constants
import { AVAILABLE_I18N_NAMESPACES, DEFAULT_COUNTRY, USER_ACTIONS } from '../../../constants/base.constants';

// Styles
import * as S from './LeavePageWidget.styles';


/**
 * Component
 */

export const LeavePageWidget = memo(({
	className
}) => {

	// Create state handlers
	const [scriptEnabled, setScriptEnabled] = useState(false);

	// Get partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current locale from hook
	const { cookieBarHeight } = useSelector((state) => state.dimensions.value);

	// Get resources manifest from hook
	const escapeUrls = useSelector((state) => state.resourceManifest.escapeUrls);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get partner content
	const resourceLink = useMemo(() => partner?.navigation?.links?.resources, [partner]);

	// Get escape urls from manifest
	const escapeManfiest = useMemo(() => {
		if (escapeUrls) {
			return escapeUrls[locale.countryId] || escapeUrls[DEFAULT_COUNTRY];
		}
		return {
			replaceUrl: 'https://www.amazon.com/b/?_encoding=UTF8&node=1063278&ref_=cct_cg_Explore_1a1&pf_rd_p=a55f207c-6283-46d9-aeb3-4967ef77df9c&pf_rd_r=AGN3GQJSGC6CBVN6TW83',
			newTabUrl: 'https://www.allrecipes.com/recipes/15436/everyday-cooking/one-pot-meals/'
		};
	}, [locale, escapeUrls]);

	// Handle analytics capture
	const captureAnalytics = (action) => {

		// Record action
		recordAction({
			action,
			urlPath: window.location.pathname,
		});
	};

	// Handle exit
	const handleExit = () => {

		// Capture analytics
		captureAnalytics(USER_ACTIONS.FAB_SAFETY_EXIT_CLICK);

		// Destroy markup
		document.body.innerHTML = '';

		// Open new tab url
		window.open(escapeManfiest.newTabUrl, '_newtab');

		// Replace url
		window.location.replace(escapeManfiest?.replaceUrl);
	};

	// Handle actions on component load
	useEffect(() => {

		// Set script enabled
		setScriptEnabled(true);

	}, []);

	// Render component
	return (
		<S.Wrapper className={className ? `${className} animate` : 'animate'} $offset={cookieBarHeight}>

			{/* Exit Fab */}
			<Padding>
				<noscript>
					<LocaleLink
						to={escapeManfiest?.replaceUrl}
						title={t('leavePageWidget.exit')}
						aria-label={t('leavePageWidget.exit')}
						onClick={() => { captureAnalytics(USER_ACTIONS.FAB_SAFETY_EXIT_CLICK); }}
					>
						<S.LeavePageFab className="animate">
							<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
							<Typography tag="h4" weight="semibold">
								<Trans t={t} i18nKey="leavePageWidget.exit">
									Safety Exit
								</Trans>
							</Typography>
						</S.LeavePageFab>
					</LocaleLink>
				</noscript>
				{scriptEnabled && (
					<S.LeavePageFab className="animate" onClick={handleExit}>
						<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
						<Typography tag="h4" weight="semibold">
							<Trans t={t} i18nKey="leavePageWidget.exit">
								Safety Exit
							</Trans>
						</Typography>
					</S.LeavePageFab>
				)}
			</Padding>

			{/* Resources Fab */}
			<Padding>
				<LocaleLink
					to={resourceLink?.url || '/resources'}
					target={resourceLink?.new_tab ? '_blank' : null}
					title={t('leavePageWidget.resources')}
					aria-label={t('leavePageWidget.resources')}
					onClick={() => { captureAnalytics(USER_ACTIONS.FAB_RESOURCES_CLICK); }}
				>
					<S.LeavePageFab className="resourcesFab animate">
						<Typography tag="h4" weight="semibold">
							<Trans t={t} i18nKey="leavePageWidget.resources">
								Resources
							</Trans>
						</Typography>
					</S.LeavePageFab>
				</LocaleLink>
			</Padding>
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

LeavePageWidget.displayName = 'LeavePageWidget';
LeavePageWidget.propTypes = {
	className: PropTypes.string
};
LeavePageWidget.defaultProps = {
	className: null,
};
